import { ref } from 'vue'
import { getFCCData } from '@/util/fcc/FCCUtil'
import { fccUrlBuilder } from '@/util/fcc/urlBuilder'
import {
  Alert,
  Merchant,
  FCCApiResponse,
  FCCApiParams,
  FCCAlertsArrayResponse,
  FCCAlertHistoryResponse,
  FCCMerchantsResponse,
  AlertHistory,
  MerchantEventTolerances,
  ActionRequest,
  AvailableAction,
  EventTypes,
  FCCEventTypesResponse,
  FCCActionsResponse,
  Case,
  JiraIssue,
  JiraCommentsResponse,
  BulkActionsRequest,
  ReserveMetrics,
} from '@/types/fcc'
import { AuthState } from '@/types/authState'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

/**
 * Map the reserve-metrics endpoint's Snowflake-style lowercase keys to the
 * UPPER_SNAKE_CASE convention the rest of the FCC code uses. Falls back to
 * the uppercase key if the API ever changes its casing, so this stays
 * forward-compatible.
 */
function normalizeReserveMetrics(
  raw: Record<string, unknown>,
): ReserveMetrics {
  const pickNumber = (low: string, up: string): number | null | undefined => {
    const value = low in raw ? raw[low] : raw[up]
    if (value === null) return null
    if (value === undefined) return undefined
    const parsed = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  const pickString = (low: string, up: string): string | null | undefined => {
    const value = low in raw ? raw[low] : raw[up]
    if (value === null) return null
    if (value === undefined) return undefined
    return String(value)
  }
  return {
    MAX_AMOUNT: pickNumber('max_amount', 'MAX_AMOUNT'),
    PERCENT_HOLDBACK: pickNumber('percent_holdback', 'PERCENT_HOLDBACK'),
    OVERRIDE_MAX_AMOUNT: pickNumber('override_max_amount', 'OVERRIDE_MAX_AMOUNT'),
    AMOUNT_COLLECTED: pickNumber('amount_collected', 'AMOUNT_COLLECTED'),
    LAST_UPDATED_DATE: pickString('last_updated_date', 'LAST_UPDATED_DATE'),
    DISABLED_ON: pickString('disabled_on', 'DISABLED_ON'),
    DEFAULT_CURRENCY: pickString('default_currency', 'DEFAULT_CURRENCY'),
  }
}

export const useFCCApi = () => {
  const loading = ref(false)
  const error = ref<ApiError | null>(null)

  const getAlerts = async (
    authState: AuthState,
    params?: FCCApiParams,
  ): Promise<Alert[]> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      // Map optional merchantId to backend's expected merchant_id if provided
      const queryParams = {
        ...(params || {}),
        ...(params?.merchantId && !params?.merchant_id
          ? { merchant_id: params.merchantId }
          : {}),
      } as Record<string, unknown>
      const response = await getFCCData<FCCAlertsArrayResponse>(
        token,
        API_ENDPOINTS.FCC.ALERTS,
        'get',
        queryParams,
      )
      return response || []
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getMerchants = async (
    authState: AuthState,
    params?: FCCApiParams,
  ): Promise<Merchant[]> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const response = await getFCCData<FCCMerchantsResponse>(
        token,
        API_ENDPOINTS.FCC.MERCHANTS,
        'get',
        params as Record<string, unknown>,
      )
      return response?.array || []
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getMerchantDetails = async (
    authState: AuthState,
    merchantId: string,
  ): Promise<Merchant> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.merchantDetails(merchantId)

      const response = await getFCCData<FCCApiResponse<Merchant>>(
        token,
        endpoint,
        'get',
      )

      // Handle different response structures
      if (response && typeof response === 'object') {
        // If response has a data property, use it
        if ('data' in response && response.data) {
          const merchant = response.data as unknown as Merchant
          // Normalize CURRENT_EVENTS date field: TIMESTAMP -> EVENT_DATE
          if (Array.isArray(merchant.CURRENT_EVENTS)) {
            merchant.CURRENT_EVENTS = merchant.CURRENT_EVENTS.map(evt => {
              const { TIMESTAMP, EVENT_DATE } = (evt || {}) as {
                TIMESTAMP?: string
                EVENT_DATE?: string
              }
              const normalizedEventDate = EVENT_DATE ?? TIMESTAMP ?? ''
              const base = evt as unknown as AlertHistory
              return { ...base, EVENT_DATE: normalizedEventDate }
            })
          }
          return merchant
        }
        // If response is the merchant object directly
        if ('MERCHANT_ID' in response) {
          const merchant = response as unknown as Merchant
          if (Array.isArray(merchant.CURRENT_EVENTS)) {
            merchant.CURRENT_EVENTS = merchant.CURRENT_EVENTS.map(evt => {
              const { TIMESTAMP, EVENT_DATE } = (evt || {}) as {
                TIMESTAMP?: string
                EVENT_DATE?: string
              }
              const normalizedEventDate = EVENT_DATE ?? TIMESTAMP ?? ''
              const base = evt as unknown as AlertHistory
              return { ...base, EVENT_DATE: normalizedEventDate }
            })
          }
          return merchant
        }
        // Unexpected object shape
        throw new Error('Invalid response structure from merchant details API')
      } else {
        throw new Error('Invalid response structure from merchant details API')
      }
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getEventTypes = async (authState: AuthState): Promise<EventTypes> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const response = await getFCCData<FCCEventTypesResponse>(
        token,
        API_ENDPOINTS.FCC.EVENT_TYPES,
        'get',
      )
      return response?.EVENT_TYPES || []
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getAvailableActions = async (
    authState: AuthState,
  ): Promise<AvailableAction[]> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const response = await getFCCData<FCCActionsResponse>(
        token,
        API_ENDPOINTS.FCC.AVAILABLE_ACTIONS,
        'get',
      )
      return response?.ACTIONS || []
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getMerchantAlertHistory = async (
    authState: AuthState,
    merchantId: string,
    page = 1,
    pageSize = 20,
  ): Promise<{
    alerts: AlertHistory[]
    pagination: {
      page: number
      pageSize: number
      total: number
      totalPages: number
    }
  }> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.merchantAlertHistory(merchantId)
      const response = await getFCCData<FCCAlertHistoryResponse>(
        token,
        endpoint,
        'get',
        { page, page_size: pageSize },
      )
      return {
        alerts: response?.ALERTS || [],
        pagination: {
          page: response?.PAGE || page,
          pageSize: response?.PAGE_SIZE || pageSize,
          total: response?.TOTAL_ALERTS || 0,
          totalPages: response?.TOTAL_PAGES || 0,
        },
      }
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getMerchantEventTolerances = async (
    authState: AuthState,
    merchantId: string,
  ): Promise<MerchantEventTolerances> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.merchantEventTolerances(merchantId)
      const response = await getFCCData<
        FCCApiResponse<MerchantEventTolerances>
      >(token, endpoint, 'get')
      return response.data
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const updateMerchantEventTolerance = async (
    authState: AuthState,
    merchantId: string,
    eventType: string,
    tolerance: string,
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.updateMerchantEventTolerance(merchantId)
      await getFCCData<FCCApiResponse<void>>(token, endpoint, 'put', {
        EVENT_TYPE: eventType,
        TOLERANCE: tolerance,
      })
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const updateTolerance = async (
    authState: AuthState,
    merchantId: string,
    tolerance: string,
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.updateMerchantTolerance(merchantId)
      await getFCCData<FCCApiResponse<void>>(token, endpoint, 'put', {
        tolerance,
      })
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const enableReserve = async (
    authState: AuthState,
    merchantId: number,
    payload?: { percent_holdback: number; max_amount: number },
  ): Promise<{ message: string }> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.enableReserve(merchantId)
      // The re-enable form always supplies { percent_holdback, max_amount } as
      // the PATCH body; if omitted, an empty body ({}) is sent.
      const response = await getFCCData<{ message: string }>(
        token,
        endpoint,
        'patch',
        payload ?? {},
      )
      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getMerchantReserveMetrics = async (
    authState: AuthState,
    merchantId: number,
  ): Promise<ReserveMetrics | null> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.merchantReserveMetrics(merchantId)
      const response = await getFCCData<
        ReserveMetrics | FCCApiResponse<ReserveMetrics>
      >(token, endpoint, 'get')
      // A 200 means the merchant has a reserve record (is in the reserve
      // table), even if individual fields are empty. Some FCC endpoints wrap
      // the payload in `{ data: ... }`; unwrap if so. A falsy 200 body still
      // counts as "in table" → normalize an empty object.
      const raw =
        response &&
        typeof response === 'object' &&
        'data' in response &&
        response.data
          ? (response.data as Record<string, unknown>)
          : ((response as Record<string, unknown>) ?? {})
      return normalizeReserveMetrics(raw)
    } catch (err) {
      const apiError = normalizeError(err)
      // A 404 means this merchant has no reserve record — i.e. is not in the
      // reserve table. Return null (the authoritative "no reserve" signal),
      // distinct from a genuine failure which rethrows below.
      if (apiError.isNotFoundError()) {
        return null
      }
      error.value = apiError
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const setReservePersona = async (
    authState: AuthState,
    merchantId: number,
    payload: { persona_case_id: string; website_number: number },
  ): Promise<{ message?: string }> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.setReservePersona(merchantId)
      const response = await getFCCData<{ message?: string }>(
        token,
        endpoint,
        'post',
        payload,
      )
      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const setReserveManual = async (
    authState: AuthState,
    merchantId: number,
    payload: { percent_holdback: number; max_amount: number },
  ): Promise<{ message?: string }> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.setReserveManual(merchantId)
      const response = await getFCCData<{ message?: string }>(
        token,
        endpoint,
        'post',
        payload,
      )
      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const updateMaxAmount = async (
    authState: AuthState,
    merchantId: number,
    maxAmount: number,
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.updateMerchantMaxAmount(merchantId)
      await getFCCData<FCCApiResponse<void>>(token, endpoint, 'put', {
        max_amount: maxAmount,
      })
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const updatePercentHoldback = async (
    authState: AuthState,
    merchantId: number,
    payload: { percent_holdback: number },
  ): Promise<{ message?: string }> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.updatePercentHoldback(merchantId)
      const response = await getFCCData<{ message?: string }>(
        token,
        endpoint,
        'put',
        payload,
      )
      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const executeAction = async (
    authState: AuthState,
    alertId: string,
    action: ActionRequest,
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.alertActions(alertId)
      await getFCCData<FCCApiResponse<void>>(
        token,
        endpoint,
        'post',
        action as unknown as Record<string, unknown>,
      )
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const executeBulkActions = async (
    authState: AuthState,
    actions: BulkActionsRequest,
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      await getFCCData<FCCApiResponse<void>>(
        token,
        API_ENDPOINTS.FCC.BULK_ACTIONS,
        'post',
        actions as unknown as Record<string, unknown>,
      )
    } catch (err) {
      error.value = normalizeError(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  // Cases API methods
  const getCases = async (
    authState: AuthState,
    params?: FCCApiParams,
  ): Promise<Case[]> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const response = await getFCCData<Case[]>(
        token,
        API_ENDPOINTS.FCC.CASES,
        'get',
        params as Record<string, unknown>,
      )
      // Return the API response directly as it already matches our Case interface
      return response || []
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getCase = async (
    authState: AuthState,
    caseId: string,
  ): Promise<Case> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.caseDetails(caseId)
      const response = await getFCCData<Case>(token, endpoint, 'get')
      // Return the API response directly as it already matches our Case interface
      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const closeCase = async (
    authState: AuthState,
    caseId: string,
  ): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.caseClose(caseId)
      await getFCCData<FCCApiResponse<void>>(token, endpoint, 'put')
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  // JIRA Issues API methods
  const getJiraIssue = async (
    authState: AuthState,
    issueId: string,
  ): Promise<JiraIssue> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.jiraIssue(issueId)
      const response = await getFCCData<JiraIssue>(token, endpoint, 'get')
      return response
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  const getJiraIssueComments = async (
    authState: AuthState,
    issueId: string,
  ): Promise<JiraCommentsResponse> => {
    loading.value = true
    error.value = null

    try {
      const token = authState.user?.accessToken ?? ''
      const endpoint = fccUrlBuilder.jiraIssueComments(issueId)
      const response = await getFCCData<JiraCommentsResponse>(
        token,
        endpoint,
        'get',
      )
      // API returns array directly, no wrapper object
      return response || []
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getAlerts,
    getMerchants,
    getMerchantDetails,
    getMerchantAlertHistory,
    getMerchantEventTolerances,
    updateMerchantEventTolerance,
    getEventTypes,
    getAvailableActions,
    updateTolerance,
    enableReserve,
    getMerchantReserveMetrics,
    setReservePersona,
    setReserveManual,
    updateMaxAmount,
    updatePercentHoldback,
    executeAction,
    executeBulkActions,
    // Cases API
    getCases,
    getCase,
    closeCase,
    // JIRA Issues API
    getJiraIssue,
    getJiraIssueComments,
  }
}
